import React, { useEffect, useRef, useState } from 'react';
import './DeleteMessageModal.css';

/**
 * Slack-parity delete confirmation modal. Replaces the legacy
 * `window.confirm` call so the destructive action gets a proper dialog
 * with the message preview, a clear visual hierarchy, and a focused
 * destructive button.
 *
 * Behavior:
 * - Initial focus lands on the Delete button so Enter commits and Esc
 *   cancels — matches Slack's keyboard model for destructive prompts.
 * - Esc and backdrop-click close without firing onConfirm.
 * - Focus returns to the trigger (kebab) via `returnFocusRef`.
 * - Awaits onConfirm before closing so parents can surface errors
 *   without the modal disappearing first.
 */
function DeleteMessageModal({
  message,
  onClose,
  onConfirm,
  returnFocusRef
}) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const deleteBtnRef = useRef(null);
  const dialogRef = useRef(null);

  // Slack puts the focus ring on the destructive action on open. Enter
  // commits, Esc cancels — same model as `window.confirm`, but visible.
  useEffect(() => {
    if (deleteBtnRef.current && typeof deleteBtnRef.current.focus === 'function') {
      deleteBtnRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleClose() {
    if (returnFocusRef?.current && typeof returnFocusRef.current.focus === 'function') {
      try {
        returnFocusRef.current.focus();
      } catch (_err) {
        // Focus restore is best-effort.
      }
    }
    onClose();
  }

  async function handleConfirm() {
    if (submitting) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      await onConfirm();
      handleClose();
    } catch (err) {
      setErrorMessage(err?.message || 'Failed to delete message.');
      setSubmitting(false);
    }
  }

  const senderName = message?.senderDisplayName || message?.senderId || 'Unknown';
  const initials = getInitials(senderName);
  const timestampLabel = formatTimestamp(message?.createdAt || message?.timestamp);
  const previewText = (message?.content || '').trim();

  return (
    <div
      className="delete-message-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        ref={dialogRef}
        className="delete-message-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-message-modal-title"
        aria-describedby="delete-message-modal-body"
      >
        <header className="delete-message-modal-header">
          <h2 id="delete-message-modal-title">Delete message</h2>
          <button
            type="button"
            className="delete-message-modal-close"
            aria-label="Close"
            onClick={handleClose}
          >
            ×
          </button>
        </header>

        <div className="delete-message-modal-body">
          <p id="delete-message-modal-body" className="delete-message-modal-prompt">
            Are you sure you want to delete this message? This cannot be undone.
          </p>

          <div className="delete-message-modal-preview" aria-label="Message preview">
            <div className="delete-message-modal-preview-avatar" aria-hidden="true">
              {initials}
            </div>
            <div className="delete-message-modal-preview-body">
              <div className="delete-message-modal-preview-meta">
                <span className="delete-message-modal-preview-sender">{senderName}</span>
                {timestampLabel && (
                  <span className="delete-message-modal-preview-time">{timestampLabel}</span>
                )}
              </div>
              {previewText && (
                <div className="delete-message-modal-preview-content">
                  {truncate(previewText, 240)}
                </div>
              )}
              {!previewText && Array.isArray(message?.attachments) && message.attachments.length > 0 && (
                <div className="delete-message-modal-preview-content delete-message-modal-preview-content--meta">
                  {message.attachments.length === 1
                    ? '1 attachment'
                    : `${message.attachments.length} attachments`}
                </div>
              )}
            </div>
          </div>

          {errorMessage && (
            <p className="delete-message-modal-error" role="alert">{errorMessage}</p>
          )}
        </div>

        <div className="delete-message-modal-actions">
          <button
            type="button"
            className="delete-message-modal-btn"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            ref={deleteBtnRef}
            type="button"
            className="delete-message-modal-btn delete-message-modal-btn--destructive"
            onClick={handleConfirm}
            disabled={submitting}
            autoFocus
          >
            {submitting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function getInitials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

function formatTimestamp(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const monthDay = d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  // Mirror Slack: "May 7th at 10:41 AM" → we render the locale's natural
  // equivalent without faking ordinals in non-English locales.
  return `${monthDay} at ${time}`;
}

function truncate(text, max) {
  if (!text) return '';
  if (text.length <= max) return text;
  return text.slice(0, max - 1).trimEnd() + '…';
}

export default DeleteMessageModal;
